/* Metal Price Tracker widget
 *
 * Renders with TradingView Lightweight Charts (Apache-2.0; the small attribution mark
 * on the chart is part of its licence and must stay).
 *
 * SOLWE: Laatukoru fork, served from theme assets by sections/metal-price-tracker.liquid.
 * Based on https://metal-price-tracker.henri-silvan.workers.dev/widget.js, but the
 * widget's markup and interface text are no longer built here: the section renders
 * them from the locale files, and this script binds to the data-mpt-* hooks and fills
 * in the price data. Changes from upstream are marked "SOLWE:" below; see
 * CUSTOMIZATIONS.md #5.
 *
 * Data attributes on [data-metal-tracker]: data-locale (the store locale),
 * data-metal (XAU|XAG|XPT|XPD), data-currency (EUR|USD),
 * data-range (24h|1w|1m|3m|1y|3y|5y|10y), data-unit (oz|g|kg), data-type (line|candle),
 * data-api (base URL, required: the script's own URL is Shopify's CDN, not the API),
 * data-buy="14K" (open in our-buying-price mode for that karat; gold only).
 * Runtime strings come from the <script type="application/json" data-mpt-strings>
 * inside it.
 *
 * Upstream synced 2026-10-22 (widget.js, 446 lines): adds the buying-price mode.
 */
(function () {
  'use strict';

  // SOLWE: the section is rendered once per instance, so this file can be included
  // more than once on a page. Run only the first copy.
  if (window.__metalTrackerLoaded) return;
  window.__metalTrackerLoaded = true;

  var OZ_IN_G = 31.1034768;
  var UNIT_FACTOR = { oz: 1, g: 1 / OZ_IN_G, kg: 1000 / OZ_IN_G };
  var METALS = ['XAU', 'XAG', 'XPT', 'XPD'];
  var RANGES = ['24h', '1w', '1m', '3m', '1y', '3y', '5y', '10y'];
  var REFRESH_MS = 5 * 60 * 1000;

  // SOLWE: the I18N table, flags and tab icon are gone. Interface text comes from the
  // locale files through the section, and the section renders the markup.
  var KARATS = [['8K', 333], ['9K', 375], ['14K', 585], ['18K', 750], ['21K', 875], ['22K', 916], ['23K', 958], ['24K', 999]];

  function ready(fn) { if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn); else fn(); }

  // SOLWE: translations are merchant-editable, so escape them before they go into innerHTML.
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }

  function fmtNumber(locale, v, decimals) {
    return new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v);
  }
  function decimalsFor(v) { v = Math.abs(v); return v >= 10000 ? 0 : v >= 10 ? 2 : 3; }

  function Widget(host) {
    var self = this;
    this.host = host;
    // SOLWE: strings come from the section's JSON block, and number and date formats
    // follow the store locale (upstream: a built-in fi/en table). A bare "en" formats
    // US-style in Intl, so it maps to en-GB, which is what upstream used.
    var locale = host.dataset.locale || 'en';
    this.lang = locale.slice(0, 2).toLowerCase();
    this.t = JSON.parse(host.querySelector('[data-mpt-strings]').textContent);
    this.t.locale = locale === 'en' ? 'en-GB' : locale;
    // SOLWE: API base always comes from data-api (upstream: the script's own URL).
    this.api = (host.dataset.api || '').replace(/\/+$/, '');
    this.state = {
      metal: METALS.indexOf(host.dataset.metal) >= 0 ? host.dataset.metal : 'XAU',
      currency: host.dataset.currency === 'USD' ? 'USD' : 'EUR',
      range: RANGES.indexOf(host.dataset.range) >= 0 ? host.dataset.range : '24h',
      unit: UNIT_FACTOR[host.dataset.unit] ? host.dataset.unit : 'g',
      type: host.dataset.type === 'candle' ? 'candle' : 'line',
      buy: false,
      karat: '14K'
    };
    var startKarat = (host.dataset.buy || '').toUpperCase();
    if (KARATS.some(function (k) { return k[0] === startKarat; })) {
      this.state.metal = 'XAU'; this.state.buy = true; this.state.karat = startKarat;
      this.state.currency = 'EUR'; this.state.unit = 'g';
    }
    this.data = null;
    this.series = null;
    this.bind(); // SOLWE: upstream called render()
    this.initChart();
    this.load();
    this.timer = setInterval(function () { if (document.visibilityState === 'visible') self.load(true); }, REFRESH_MS);
    document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'visible') self.load(true); });
  }

  // SOLWE: upstream built the whole widget here with createElement. The section now
  // renders it in Liquid, so this only finds the hooks and attaches the listeners.
  Widget.prototype.bind = function () {
    var self = this, host = this.host, s = this.state;
    var q = function (sel) { return host.querySelector(sel); };
    this.root = q('.mpt');

    this.tabs = {};
    host.querySelectorAll('[data-mpt-metal]').forEach(function (b) {
      var m = b.dataset.mptMetal;
      b.setAttribute('aria-pressed', String(m === s.metal));
      b.addEventListener('click', function () { self.set('metal', m); });
      self.tabs[m] = b;
    });

    // Set each value from state as well: a browser restoring form fields on back or
    // forward navigation could otherwise show a choice the chart is not using.
    this.selects = {};
    host.querySelectorAll('[data-mpt-select]').forEach(function (sel) {
      var key = sel.dataset.mptSelect;
      sel.value = s[key];
      sel.addEventListener('change', function () { self.set(key, sel.value); });
      self.selects[key] = sel;
    });

    this.pairEl = q('[data-mpt-pair]');
    this.ohlcEl = q('[data-mpt-ohlc]');
    this.pillEl = q('[data-mpt-pill]');
    this.chartEl = q('[data-mpt-chart]');
    this.statusEl = q('[data-mpt-status]');
    this.tipEl = q('[data-mpt-tip]');
    this.updatedEl = q('[data-mpt-updated]');
    this.sourceEl = q('[data-mpt-source]');

    // Our buying prices (gold only): checkbox, and a karat dropdown under it when ticked
    var cb = q('[data-mpt-buy-check]'), kSel = q('[data-mpt-karat]');
    cb.addEventListener('change', function () { self.setBuy(cb.checked); });
    kSel.value = s.karat;
    kSel.addEventListener('change', function () { s.karat = kSel.value; self.load(); });
    this.buyRow = q('[data-mpt-buy]'); this.buyCheck = cb; this.karatField = q('[data-mpt-karat-field]');
    this.buyNoteEl = q('[data-mpt-buy-note]');
    this.syncBuy();
  };

  // Show the buying-price row for gold only; while it is on, EUR and grams are fixed.
  Widget.prototype.syncBuy = function () {
    var s = this.state, on = s.buy && s.metal === 'XAU';
    this.buyRow.hidden = s.metal !== 'XAU';
    this.buyCheck.checked = on;
    this.karatField.hidden = !on;
    this.buyNoteEl.hidden = !on;
    this.selects.currency.disabled = on;
    this.selects.unit.disabled = on;
    this.selects.currency.value = s.currency;
    this.selects.unit.value = s.unit;
    if (this.sourceEl) this.sourceEl.textContent = on ? this.t.buySource : this.t.source;
  };

  Widget.prototype.setBuy = function (on) {
    var s = this.state;
    s.buy = on;
    if (on) { s.currency = 'EUR'; s.unit = 'g'; }
    this.syncBuy();
    this.load();
  };

  Widget.prototype.initChart = function () {
    var self = this, LWC = window.LightweightCharts;
    var css = getComputedStyle(this.root);
    var tok = function (n) { return css.getPropertyValue(n).trim(); };
    this.colors = { text: tok('--mpt-text'), muted: tok('--mpt-muted'), grid: tok('--mpt-grid'), border: tok('--mpt-border'), line: tok('--mpt-line'), up: tok('--mpt-up'), down: tok('--mpt-down'), bg: tok('--mpt-bg') };
    this.chart = LWC.createChart(this.chartEl, {
      autoSize: true,
      layout: { background: { type: 'solid', color: this.colors.bg }, textColor: this.colors.muted, fontFamily: tok('--mpt-font'), fontSize: 11, attributionLogo: true },
      grid: { vertLines: { color: this.colors.grid }, horzLines: { color: this.colors.grid } },
      rightPriceScale: { borderColor: this.colors.border },
      timeScale: { borderColor: this.colors.border, timeVisible: true, secondsVisible: false, rightOffset: 2,
        tickMarkFormatter: function (time, type) { return self.fmtTick(time, type); } },
      crosshair: { mode: LWC.CrosshairMode.Magnet, vertLine: { color: this.colors.border, labelBackgroundColor: this.colors.text }, horzLine: { color: this.colors.border, labelBackgroundColor: this.colors.text } },
      handleScroll: { mouseWheel: false, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: false, pinch: true, axisPressedMouseMove: true },
      localization: {
        locale: this.t.locale,
        priceFormatter: function (v) { return fmtNumber(self.t.locale, v, decimalsFor(v)); },
        timeFormatter: function (time) { return self.fmtWhen(time); }
      }
    });
    this.chart.subscribeCrosshairMove(function (p) { self.tooltip(p); });
    // Keep the whole range in view when the container changes width (orientation change, layout shifts).
    if (window.ResizeObserver) {
      var width = this.chartEl.clientWidth;
      new ResizeObserver(function () {
        if (self.chartEl.clientWidth !== width) {
          width = self.chartEl.clientWidth;
          // The chart resizes its canvas on its own observer; refit after it has done so.
          setTimeout(function () { if (self.series) self.fit(); }, 60);
        }
      }).observe(this.chartEl);
    }
  };

  // Chart times are shifted to the viewer's wall clock, so they are formatted as UTC.
  Widget.prototype.fmtTick = function (time, type) {
    var d = new Date(time * 1000), loc = this.t.locale;
    var pad = function (n) { return (n < 10 ? '0' : '') + n; };
    if (type === 0) return String(d.getUTCFullYear());
    if (type === 1) return new Intl.DateTimeFormat(loc, { month: 'short', timeZone: 'UTC' }).format(d);
    if (type === 2) return this.lang === 'fi' ? d.getUTCDate() + '.' + (d.getUTCMonth() + 1) + '.' : new Intl.DateTimeFormat(loc, { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(d);
    return pad(d.getUTCHours()) + ':' + pad(d.getUTCMinutes());
  };
  Widget.prototype.fmtWhen = function (time) {
    var intraday = this.state.range === '24h' || this.state.range === '1w';
    var d = new Date(time * 1000), loc = this.t.locale;
    var date = new Intl.DateTimeFormat(loc, { dateStyle: 'medium', timeZone: 'UTC' }).format(d);
    return intraday ? date + ' ' + this.fmtTick(time, 3) : date;
  };

  // Fit the whole series with a little room on both sides, so the first and last axis
  // labels are not clipped at the canvas edges (fitContent alone cuts "21.9." to "1.9.").
  Widget.prototype.fit = function () {
    var d = this.data; if (!d) return;
    var n = (this.state.type === 'candle' ? d.candles : d.line).length;
    var pad = Math.max(2, Math.ceil(n * 0.03));
    this.chart.timeScale().setVisibleLogicalRange({ from: -pad, to: n - 1 + pad });
  };

  Widget.prototype.set = function (key, value) {
    if (this.state[key] === value) return;
    this.state[key] = value;
    if (key === 'metal') {
      var tabs = this.tabs;
      // SOLWE: metal buttons are a toggle group (aria-pressed), not tabs; upstream used
      // role="tab" without the tab panel or arrow-key handling that role requires.
      METALS.forEach(function (m) { tabs[m].setAttribute('aria-pressed', String(m === value)); });
      if (value !== 'XAU') this.state.buy = false;
      this.syncBuy();
    }
    if (key === 'metal' || key === 'currency' || key === 'range') this.load();
    else this.draw();
  };

  Widget.prototype.load = function (silent) {
    var self = this, s = this.state;
    var url = this.api + '/api/series?metal=' + s.metal + '&currency=' + s.currency + '&range=' + s.range +
      (s.buy && s.metal === 'XAU' ? '&karat=' + s.karat : '');
    if (!silent) { this.statusEl.textContent = this.t.loading; this.statusEl.hidden = false; }
    this.pending = url;
    fetch(url).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (d) {
        if (self.pending !== url) return; // a newer request took over
        self.data = d;
        self.statusEl.hidden = true;
        self.draw();
      })
      .catch(function (err) {
        if (self.pending !== url) return;
        console.error('metal tracker', err);
        self.statusEl.textContent = self.t.error;
        self.statusEl.hidden = false;
      });
  };

  Widget.prototype.draw = function () {
    var d = this.data; if (!d) return;
    var LWC = window.LightweightCharts, s = this.state, t = this.t, self = this;
    var f = UNIT_FACTOR[s.unit];
    var conv = function (v) { return v * f; };
    var offset = function (ts) { return ts - new Date(ts * 1000).getTimezoneOffset() * 60; }; // chart shows the viewer's local time

    if (this.series) { this.chart.removeSeries(this.series); this.series = null; }
    var intraday = s.range === '24h' || s.range === '1w';
    this.chart.applyOptions({ timeScale: { timeVisible: intraday } });

    if (s.type === 'candle') {
      // Up candles hollow, down candles filled: direction is readable by shape, not colour alone.
      this.series = this.chart.addSeries(LWC.CandlestickSeries, {
        upColor: 'rgba(0,0,0,0)', downColor: this.colors.down,
        borderUpColor: this.colors.up, borderDownColor: this.colors.down,
        wickUpColor: this.colors.up, wickDownColor: this.colors.down,
        borderVisible: true, priceLineVisible: false, lastValueVisible: !d.buy
      });
      this.series.setData(d.candles.map(function (c) { return { time: offset(c.time), open: conv(c.open), high: conv(c.high), low: conv(c.low), close: conv(c.close) }; }));
    } else {
      this.series = this.chart.addSeries(LWC.LineSeries, {
        color: this.colors.line, lineWidth: 2, priceLineVisible: false, lastValueVisible: !d.buy,
        crosshairMarkerRadius: 4, crosshairMarkerBorderColor: this.colors.bg, crosshairMarkerBackgroundColor: this.colors.line
      });
      this.series.setData(d.line.map(function (p) { return { time: offset(p.time), value: conv(p.value) }; }));
    }
    this.fit();

    // Header
    var unitLabel = s.unit;
    var price = function (v) { return fmtNumber(t.locale, conv(v), decimalsFor(conv(v))) + ' ' + s.currency + ' / ' + unitLabel; };
    var buying = !!d.buy;
    this.pairEl.textContent = buying ? t.pairBuy + ' ' + d.buy.karat + ' / ' + s.currency : t.metal[s.metal] + ' / ' + s.currency;
    // SOLWE: esc() on the translated labels below.
    var parts = [[t.open, d.open], [t.close, d.current]];
    if (s.type === 'candle') parts.push([t.low, d.low], [t.high, d.high]);
    this.ohlcEl.innerHTML = parts.filter(function (p) { return p[1] != null; })
      .map(function (p) { return '<span>' + esc(p[0]) + ': <b>' + price(p[1]) + '</b></span>'; }).join('');

    var up = d.change > 0, down = d.change < 0;
    this.pillEl.className = 'mpt-pill' + (up ? ' is-up' : down ? ' is-down' : '');
    var arrow = up ? '▲' : down ? '▼' : '';
    var sign = up ? '+' : '';
    var chg = d.change == null ? '' :
      '<span class="mpt-change">' + arrow + ' ' + sign + fmtNumber(t.locale, conv(d.change), decimalsFor(conv(d.current))) + ' ' + s.currency + ' / ' + unitLabel +
      ' · ' + sign + fmtNumber(t.locale, d.changePct, 2) + ' %</span>';
    this.pillEl.innerHTML = '<span>' + esc(buying ? t.ourNow : t.now) + ' <span class="mpt-now">' + price(d.current) + '</span></span>' + chg;

    var upd = new Date(d.updated * 1000);
    this.updatedEl.textContent = t.updated + ' ' + new Intl.DateTimeFormat(t.locale, { dateStyle: 'short', timeStyle: 'short' }).format(upd);
  };

  Widget.prototype.tooltip = function (p) {
    var tip = this.tipEl;
    if (!p.point || !p.time || !this.series || !p.seriesData.has(this.series)) { tip.hidden = true; return; }
    var v = p.seriesData.get(this.series), s = this.state, t = this.t;
    var when = this.fmtWhen(p.time);
    var fmt = function (x) { return fmtNumber(t.locale, x, decimalsFor(x)); };
    // SOLWE: esc() on the translated labels.
    var body = v.value != null ? fmt(v.value) :
      esc(t.open) + ' ' + fmt(v.open) + '  ' + esc(t.high) + ' ' + fmt(v.high) + '  ' + esc(t.low) + ' ' + fmt(v.low) + '  ' + esc(t.close) + ' ' + fmt(v.close);
    tip.innerHTML = '<div class="mpt-tip-time">' + when + '</div><div>' + body + ' ' + s.currency + ' / ' + s.unit + '</div>';
    tip.hidden = false;
    var w = tip.offsetWidth, h = tip.offsetHeight, W = this.chartEl.clientWidth;
    var x = p.point.x + 16; if (x + w > W - 60) x = p.point.x - w - 16;
    var y = p.point.y - h - 12; if (y < 0) y = p.point.y + 12;
    tip.style.transform = 'translate(' + Math.max(0, x) + 'px,' + Math.max(0, y) + 'px)';
  };

  // SOLWE: upstream chain-loaded widget.css and lightweight-charts.js here. The section
  // now loads both (stylesheet_tag, then the charts file deferred before this one), so
  // boot mounts directly.
  function boot() {
    var hosts = document.querySelectorAll('[data-metal-tracker]');
    if (!hosts.length) return;
    if (!window.LightweightCharts) { console.error('metal tracker', 'metal-tracker-charts.js is not loaded'); return; }
    hosts.forEach(function (h) { if (!h.dataset.mounted) { h.dataset.mounted = '1'; (window.__metalTrackers = window.__metalTrackers || []).push(new Widget(h)); } });
  }

  ready(boot);
  // SOLWE: mount sections added or re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', boot);
})();
